import { useCallback } from 'react';
import { SkippableFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import {
    RemoteMode,
    SkipButtonActivation,
    SkipButtonType,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';
import { playerSkipButtonShowedV2 } from '@kinopoisk-int/evgen-web-events/web/player';

import { useEventCallback } from '@/scenarios/legacy-player/hooks/analytics/use-event-callback';
import { useInstanceCallback } from '@/scenarios/legacy-player/hooks/useInstanceCallback';

import { getOrientation } from '../../analytics/utils/get-orientations';

import {
    skippableFragmentTypeToSkipButtonTypeMap,
    useLogSkipButtonActivation,
} from './use-switch-to-next-episode-with-analytics';

interface PlayerSkipButtonShowedParamsBuilderProps {
    uiParams: {};
    callbackParams: [
        {
            vsid: string;
            type: SkipButtonType;
        }
    ];
}

const playerSkipButtonShowedParamsBuilder = ({
    callbackParams: [{ vsid, type }],
}: PlayerSkipButtonShowedParamsBuilderProps) => ({
    vsid,
    orientation: getOrientation(),
    remoteMode: RemoteMode.Disabled,
    type,
});

const getSkipSource = (currentFragment: SkippableFragment | undefined) =>
    skippableFragmentTypeToSkipButtonTypeMap[currentFragment?.type ?? 'UNKNOWN'];

export const useSkipAnalytics = (
    skip: () => void,
    currentFragment: SkippableFragment | undefined
) => {
    const handleSkip = useLogSkipButtonActivation();

    const handleShow = useEventCallback({
        event: playerSkipButtonShowedV2,
        eventParamsBuilder: playerSkipButtonShowedParamsBuilder,
        uiParams: {},
    });

    const onSkipButtonShow = useInstanceCallback<[element: HTMLDivElement | null], void>(
        (instance, element) => {
            const { vsid } = instance || {};
            if (element && vsid && currentFragment?.type) {
                handleShow({
                    vsid,
                    type: skippableFragmentTypeToSkipButtonTypeMap[currentFragment.type],
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        []
    );

    const skipWithAnalytics = useCallback(() => {
        if (currentFragment) {
            handleSkip({
                skipSource: getSkipSource(currentFragment),
                skippableFragmentType: SkipButtonActivation.ByUser,
            });
            skip();
        }
    }, [currentFragment, handleSkip, skip]);

    return { skipWithAnalytics, onSkipButtonShow };
};
